import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import {
  Button,
  Checkbox,
  Chip,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Popover,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { ChevronDownIcon } from "@heroicons/react/solid";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PaymentAccessKind } from "src/generated/graphql";

export type ScopeLocation = { id: number; name: string; code?: string | null };

export type LocationScopeGroup = {
  companyId: number;
  companyName: string;
  kind?: `${PaymentAccessKind}` | null;
  sessionLocationId?: number | null;
  locations: ScopeLocation[];
};

/** One business unit with the selected locations; no locations means all accessible locations of it. */
export type LocationScope = { companyId: number; locationIds: number[] };

export const SEARCH_THRESHOLD = 10;

/** Finance users see the whole session business unit; others the session location when accessible, otherwise all accessible locations. */
export const defaultLocationScope = (
  groups: LocationScopeGroup[],
  sessionCompanyId?: number | null
): LocationScope | undefined => {
  const group =
    groups.find((g) => g.companyId === sessionCompanyId) ?? groups[0];
  if (!group) return undefined;
  if (group.kind === "FINANCE")
    return { companyId: group.companyId, locationIds: [] };
  const sessionLocationId = group.sessionLocationId;
  const hasSessionLocation =
    !!sessionLocationId &&
    group.locations.some((l) => l.id === sessionLocationId);
  return {
    companyId: group.companyId,
    locationIds: hasSessionLocation ? [Number(sessionLocationId)] : [],
  };
};

const sameScope = (a: LocationScope, b: LocationScope) =>
  a.companyId === b.companyId &&
  a.locationIds.length === b.locationIds.length &&
  a.locationIds.every((id) => b.locationIds.includes(id));

// The root font size is 11px, so MUI's rem-based typography renders too small; use the px sizes used elsewhere in the app.
const PRIMARY_TEXT = { fontSize: 14 };
const SECONDARY_TEXT = { fontSize: 12 };
const BUTTON_SX = { fontSize: 12, textTransform: "none" } as const;

const locationLabel = (location: ScopeLocation) =>
  location.code ? `${location.name} (${location.code})` : location.name;

type Props = {
  groups: LocationScopeGroup[];
  value: LocationScope;
  onChange: (scope: LocationScope) => void;
  defaultScope?: LocationScope;
  /** The user's working (session) location, marked as current in the list. */
  currentLocationId?: number | null;
  /** Explains the side effect of applying the draft, e.g. a change of working location. */
  applyHint?: (draft: LocationScope) => string | undefined;
  /** Keeps the popover inside the trigger's DOM tree, for containers that close on outside clicks. */
  disablePortal?: boolean;
};

/**
 * Picks one or more locations of a single business unit. Selecting a location of another business unit
 * replaces the selection; an empty selection means all accessible locations of the business unit.
 */
const LocationScopePicker = ({
  groups,
  value,
  onChange,
  defaultScope,
  currentLocationId,
  applyHint,
  disablePortal,
}: Props) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<LocationScope>(value);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const totalLocations = groups.reduce((n, g) => n + g.locations.length, 0);
  const showSearch = totalLocations > SEARCH_THRESHOLD;
  const showGroups = groups.length > 1;

  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return groups;
    return groups
      .map((g) => ({
        ...g,
        locations: g.companyName.toLowerCase().includes(term)
          ? g.locations
          : g.locations.filter((l) =>
              locationLabel(l).toLowerCase().includes(term)
            ),
      }))
      .filter((g) => g.locations.length > 0);
  }, [groups, search]);

  const summary = useMemo(() => {
    const group = groups.find((g) => g.companyId === value.companyId);
    let text: string;
    if (value.locationIds.length === 0) {
      text = t("modules.payments.all-locations");
    } else if (value.locationIds.length === 1) {
      const location = group?.locations.find(
        (l) => l.id === value.locationIds[0]
      );
      text = location ? location.name : t("modules.payments.location");
    } else {
      text = t("modules.payments.location-scope.locations-count", {
        count: value.locationIds.length,
      });
    }
    return showGroups && group ? `${group.companyName} · ${text}` : text;
  }, [groups, value, showGroups, t]);

  const open = (event: React.MouseEvent<HTMLElement>) => {
    setDraft(value);
    setSearch("");
    setAnchor(event.currentTarget);
  };
  const close = () => setAnchor(null);
  const apply = () => {
    onChange(draft);
    close();
  };

  const toggleLocation = (group: LocationScopeGroup, locationId: number) => {
    setDraft((current) => {
      if (current.companyId !== group.companyId)
        return { companyId: group.companyId, locationIds: [locationId] };
      const locationIds = current.locationIds.includes(locationId)
        ? current.locationIds.filter((id) => id !== locationId)
        : [...current.locationIds, locationId].sort((a, b) => a - b);
      // Every location selected is the same as all locations of the business unit.
      return {
        companyId: group.companyId,
        locationIds:
          locationIds.length === group.locations.length ? [] : locationIds,
      };
    });
  };

  return (
    <>
      <button
        type="button"
        data-testid="location-scope-picker"
        aria-haspopup="dialog"
        aria-label={t("modules.payments.location-scope.label")}
        onClick={open}
        className="flex w-full min-w-0 items-center px-2 py-2 font-medium text-gray-700 hover:text-gray-500 bg-white whitespace-nowrap focus:outline-none focus-visible:ring-2"
        title={summary}
      >
        <LocationOnOutlinedIcon className="shrink-0" />
        <span className="min-w-0 flex-1 truncate pl-1 text-left">{summary}</span>
        <ChevronDownIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
      </button>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 360, maxWidth: "calc(100vw - 32px)" } }}
        TransitionProps={{ onEntered: () => searchRef.current?.focus() }}
        disablePortal={disablePortal}
      >
        <div role="dialog" aria-label={t("modules.payments.location-scope.label")}>
          {showSearch && (
            <div className="p-3 pb-1">
              <TextField
                inputRef={searchRef}
                fullWidth
                size="small"
                sx={{ "& .MuiInputBase-input": PRIMARY_TEXT }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("modules.payments.location-scope.search")}
                inputProps={{
                  "aria-label": t("modules.payments.location-scope.search"),
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          )}
          {showGroups && (
            <p className="px-4 pt-2 text-[12px] text-gray-500">
              {t("modules.payments.location-scope.one-business-unit")}
            </p>
          )}
          <List dense sx={{ maxHeight: 360, overflowY: "auto" }}>
            {filteredGroups.length === 0 && (
              <p className="px-4 py-3 text-[14px] text-gray-500">
                {t("modules.payments.location-scope.no-match")}
              </p>
            )}
            {filteredGroups.map((group) => {
              const isCurrent = draft.companyId === group.companyId;
              const allSelected = isCurrent && draft.locationIds.length === 0;
              return (
                <li key={group.companyId}>
                  <ul className="p-0">
                    {showGroups && (
                      <ListSubheader
                        sx={{ ...SECONDARY_TEXT, lineHeight: "32px", fontWeight: 600 }}
                      >
                        {group.companyName}
                      </ListSubheader>
                    )}
                    <ListItemButton
                      selected={allSelected}
                      onClick={() =>
                        setDraft({ companyId: group.companyId, locationIds: [] })
                      }
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          size="small"
                          checked={allSelected}
                          indeterminate={isCurrent && draft.locationIds.length > 0}
                          tabIndex={-1}
                          disableRipple
                          inputProps={{
                            "aria-label": showGroups
                              ? `${t("modules.payments.all-locations")} - ${group.companyName}`
                              : t("modules.payments.all-locations"),
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={t("modules.payments.all-locations")}
                        primaryTypographyProps={PRIMARY_TEXT}
                      />
                    </ListItemButton>
                    {group.locations.map((location) => {
                      const checked =
                        isCurrent && draft.locationIds.includes(location.id);
                      return (
                        <ListItemButton
                          key={location.id}
                          onClick={() => toggleLocation(group, location.id)}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox
                              edge="start"
                              size="small"
                              checked={checked}
                              tabIndex={-1}
                              disableRipple
                              inputProps={{ "aria-label": locationLabel(location) }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={location.name}
                            secondary={location.code}
                            primaryTypographyProps={PRIMARY_TEXT}
                            secondaryTypographyProps={SECONDARY_TEXT}
                          />
                          {location.id === currentLocationId && (
                            <Chip
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={SECONDARY_TEXT}
                              label={t("modules.payments.location-scope.current")}
                            />
                          )}
                        </ListItemButton>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
          </List>
          {!sameScope(draft, value) && applyHint?.(draft) && (
            <p
              className="border-t px-4 py-2 text-[12px] text-gray-600"
              data-testid="location-scope-apply-hint"
            >
              {applyHint(draft)}
            </p>
          )}
          <div className="flex items-center justify-between gap-2 border-t px-3 py-2">
            {defaultScope &&
            defaultScope.locationIds.length > 0 &&
            !sameScope(draft, defaultScope) ? (
              <Button
                size="small"
                sx={BUTTON_SX}
                onClick={() => setDraft(defaultScope)}
              >
                {t("modules.payments.location-scope.my-location")}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button size="small" sx={BUTTON_SX} onClick={close}>
                {t("modules.payments.location-scope.cancel")}
              </Button>
              <Button
                size="small"
                variant="contained"
                sx={BUTTON_SX}
                disabled={sameScope(draft, value)}
                onClick={apply}
              >
                {t("modules.payments.location-scope.apply")}
              </Button>
            </div>
          </div>
        </div>
      </Popover>
    </>
  );
};

export default LocationScopePicker;
